import React, { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  BADGE_POSITIONS,
  BADGE_TONES,
  CARD_VARIANTS,
  PlanCard,
  SUPPORTED_LANGUAGES,
  type CardBadge,
  type CardBlock,
  type CardBlockType,
  type CardContent,
  type CardLanguage,
  type FeatureItem,
} from '@/components/PlanCard'
import LocalizedInput, { ErrorFieldContext } from './LocalizedInput'
import { BLOCK_LABELS, CREATE_TEMPLATE, SAMPLE_PLANS, UPDATE_TEMPLATE, blankText, cardCoverage, newBlock, serverError, starterContent, type PlanCardTemplate } from './editing'

/**
 * Builds a card template: style, badge and an ordered list of blocks, with a live
 * preview drawn by the same PlanCard the paywall uses. The JSON is never shown; the
 * server validates it on save and names the field it refuses.
 */

const VARIANT_LABELS: Record<CardContent['variant'], string> = { default: 'Default', featured: 'Featured (dark)', muted: 'Muted (coming soon)' }
const TONE_LABELS: Record<CardBadge['tone'], string> = { highlight: 'Lime', outline: 'White', subtle: 'Soft' }
const POSITION_LABELS: Record<CardBadge['position'], string> = { top: 'Top', corner: 'Corner' }
const MAX_FEATURES = 15

/** Optional texts left blank are dropped, so the server does not ask for their English. */
export const cleanContent = (content: CardContent): CardContent => ({
  ...content,
  blocks: content.blocks.map((block) => {
    if (block.type === 'price') {
      const { periodLabel, note, ...rest } = block
      return { ...rest, ...(blankText(periodLabel) ? {} : { periodLabel }), ...(blankText(note) ? {} : { note }) }
    }
    if (block.type === 'features') {
      return { ...block, items: block.items.map(({ text, tag }) => ({ text, ...(blankText(tag) ? {} : { tag }) })) }
    }
    return block
  }),
})

const Section: React.FC<{ title: string; children: React.ReactNode; actions?: React.ReactNode }> = ({ title, children, actions }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4">
    <div className="mb-3 flex items-center justify-between">
      <h5 className="font-semibold text-gray-800">{title}</h5>
      {actions}
    </div>
    {children}
  </div>
)

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => <label className="mb-1 block text-[11px] font-medium text-gray-600">{children}</label>

const Choice = <T extends string>({ value, options, labels, onChange }: { value: T; options: readonly T[]; labels: Record<T, string>; onChange: (v: T) => void }) => (
  <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-0.5">
    {options.map((option) => (
      <button
        key={option}
        type="button"
        onClick={() => onChange(option)}
        className={cn('rounded-md px-2.5 py-1 text-xs font-medium', value === option ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900')}
      >
        {labels[option]}
      </button>
    ))}
  </div>
)

export const LanguageSwitch: React.FC<{ value: CardLanguage; onChange: (l: CardLanguage) => void; coverage?: Record<string, [number, number]> }> = ({ value, onChange, coverage }) => (
  <div className="inline-flex gap-1 rounded-lg bg-gray-100 p-0.5">
    {SUPPORTED_LANGUAGES.map((lang) => {
      const [filled, total] = coverage?.[lang] ?? [0, 0]
      const complete = filled === total
      return (
        <button
          key={lang}
          type="button"
          onClick={() => onChange(lang)}
          className={cn('rounded-md px-2.5 py-1 text-xs font-semibold uppercase', value === lang ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900')}
          title={coverage ? `${filled} of ${total} texts translated` : undefined}
        >
          {lang}
          {coverage && <span className={cn('ml-1 font-normal normal-case', complete ? 'text-emerald-600' : 'text-amber-600')}>{complete ? '✓' : `${filled}/${total}`}</span>}
        </button>
      )
    })}
  </div>
)

interface TemplateEditorProps {
  template: PlanCardTemplate | null
  onSaved: (template: PlanCardTemplate) => void
  onCancel: () => void
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onSaved, onCancel }) => {
  const [name, setName] = useState(template?.name ?? '')
  const [content, setContent] = useState<CardContent>(() => template?.content ?? starterContent())
  const [language, setLanguage] = useState<CardLanguage>('en')
  const [sample, setSample] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<{ message: string; field: string | null } | null>(null)

  const coverage = useMemo(() => cardCoverage(content), [content])
  const preview = useMemo(() => cleanContent(content), [content])

  const setBlocks = (update: (blocks: CardBlock[]) => CardBlock[]) => setContent((c) => ({ ...c, blocks: update(c.blocks) }))
  const setBlock = (index: number, block: CardBlock) => setBlocks((blocks) => blocks.map((b, i) => (i === index ? block : b)))
  const moveBlock = (index: number, direction: -1 | 1) =>
    setBlocks((blocks) => {
      const target = index + direction
      if (target < 0 || target >= blocks.length) return blocks
      const next = [...blocks]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  const removeBlock = (index: number) => setBlocks((blocks) => blocks.filter((_, i) => i !== index))
  const addBlock = (type: CardBlockType) => {
    setBlocks((blocks) => {
      const block = newBlock(type, blocks)
      // A new block goes above the button, which stays last.
      const cta = blocks.findIndex((b) => b.type === 'cta')
      return cta === -1 ? [...blocks, block] : [...blocks.slice(0, cta), block, ...blocks.slice(cta)]
    })
  }

  const has = (type: CardBlockType) => content.blocks.some((b) => b.type === type)
  const addable = (Object.keys(BLOCK_LABELS) as CardBlockType[]).filter((type) => !(type === 'price' || type === 'cta') || !has(type))

  const save = async () => {
    if (template && template.planCount > 0) {
      const ok = confirm(`This changes the card of ${template.planCount} plan(s) in ${template.organisationCount} organisation(s), immediately. Save?`)
      if (!ok) return
    }
    setError(null)
    setSaving(true)
    try {
      const body = cleanContent(content)
      const response = template
        ? await authorizedGraphQLRequest<{ updatePlanCardTemplate: PlanCardTemplate }>(UPDATE_TEMPLATE, { id: template.id, name: name.trim(), content: body })
        : await authorizedGraphQLRequest<{ createPlanCardTemplate: PlanCardTemplate }>(CREATE_TEMPLATE, { name: name.trim(), content: body })
      onSaved('updatePlanCardTemplate' in response ? response.updatePlanCardTemplate : response.createPlanCardTemplate)
    } catch (err) {
      setError(serverError(err, 'Failed to save the template'))
    } finally {
      setSaving(false)
    }
  }

  const renderFeatures = (block: Extract<CardBlock, { type: 'features' }>, index: number) => {
    const setItems = (items: FeatureItem[]) => setBlock(index, { ...block, items })
    const path = `blocks[${index}].items`
    return (
      <div className="space-y-2">
        {block.items.map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="grid flex-1 gap-1.5 sm:grid-cols-[1fr_7rem]">
              <LocalizedInput
                language={language}
                path={`${path}[${i}].text`}
                value={item.text}
                placeholder="**Bold lead** — the rest"
                onChange={(text) => setItems(block.items.map((it, j) => (j === i ? { ...it, text } : it)))}
              />
              <LocalizedInput
                language={language}
                path={`${path}[${i}].tag`}
                value={item.tag}
                placeholder="Tag (e.g. Soon)"
                optional
                tokens={false}
                onChange={(tag) => setItems(block.items.map((it, j) => (j === i ? { ...it, tag } : it)))}
              />
            </div>
            <div className="flex pt-1">
              <button
                type="button"
                disabled={i === 0}
                onClick={() => setItems(block.items.map((it, j) => (j === i - 1 ? block.items[i] : j === i ? block.items[i - 1] : it)))}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
                title="Move up"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button type="button" onClick={() => setItems(block.items.filter((_, j) => j !== i))} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600" title="Remove feature">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {block.items.length < MAX_FEATURES && (
          <button type="button" onClick={() => setItems([...block.items, { text: { en: '' } }])} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            <Plus className="h-3 w-3" /> Add feature
          </button>
        )}
      </div>
    )
  }

  const renderBlockFields = (block: CardBlock, index: number) => {
    const path = `blocks[${index}]`
    switch (block.type) {
      case 'title':
      case 'tagline':
      case 'featuresLabel':
        return <LocalizedInput language={language} path={`${path}.text`} value={block.text} multiline={block.type === 'tagline'} onChange={(text) => setBlock(index, { ...block, text })} />
      case 'price':
        return (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">The amount and currency always come from the plan.</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <Label>Period label</Label>
                <LocalizedInput
                  language={language}
                  path={`${path}.periodLabel`}
                  value={block.periodLabel}
                  inherited="per month / per year"
                  optional
                  onChange={(periodLabel) => setBlock(index, { ...block, periodLabel })}
                />
              </div>
              <div>
                <Label>Note under the price</Label>
                <LocalizedInput language={language} path={`${path}.note`} value={block.note} placeholder="Cancel any time" optional onChange={(note) => setBlock(index, { ...block, note })} />
              </div>
            </div>
          </div>
        )
      case 'divider':
        return null
      case 'features':
        return renderFeatures(block, index)
      case 'cta':
        return (
          <div className="space-y-2">
            <LocalizedInput language={language} path={`${path}.label`} value={block.label} tokens={false} onChange={(label) => setBlock(index, { ...block, label })} />
            <div className="flex flex-wrap items-center gap-3">
              <Choice value={block.style} options={['primary', 'secondary'] as const} labels={{ primary: 'Lime', secondary: 'Outline' }} onChange={(style) => setBlock(index, { ...block, style })} />
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
                <input type="checkbox" checked={block.arrow} onChange={(e) => setBlock(index, { ...block, arrow: e.target.checked })} /> Arrow
              </label>
            </div>
          </div>
        )
    }
  }

  const badge = content.badge

  return (
    <ErrorFieldContext.Provider value={error?.field ?? null}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-64 flex-1">
            <Label>Template name (only admins see it)</Label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Featured"
              className="w-full max-w-md rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-transparent focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <Label>Editing language</Label>
            <LanguageSwitch value={language} onChange={setLanguage} coverage={coverage} />
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
          <div className="space-y-4">
            <Section title="Style">
              <div className="space-y-3">
                <div>
                  <Label>Card</Label>
                  <Choice value={content.variant} options={CARD_VARIANTS} labels={VARIANT_LABELS} onChange={(variant) => setContent((c) => ({ ...c, variant }))} />
                </div>
                <div>
                  <label className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!badge}
                      onChange={(e) => setContent((c) => ({ ...c, badge: e.target.checked ? { text: { en: 'Most popular', fr: 'Le plus populaire' }, tone: 'highlight', position: 'top' } : null }))}
                    />
                    Badge
                  </label>
                  {badge && (
                    <div className="mt-2 flex flex-wrap items-start gap-3">
                      <LocalizedInput
                        className="min-w-56 flex-1"
                        language={language}
                        path="badge.text"
                        value={badge.text}
                        tokens={false}
                        onChange={(text) => setContent((c) => ({ ...c, badge: { ...badge, text } }))}
                      />
                      <Choice value={badge.tone} options={BADGE_TONES} labels={TONE_LABELS} onChange={(tone) => setContent((c) => ({ ...c, badge: { ...badge, tone } }))} />
                      <Choice value={badge.position} options={BADGE_POSITIONS} labels={POSITION_LABELS} onChange={(position) => setContent((c) => ({ ...c, badge: { ...badge, position } }))} />
                    </div>
                  )}
                </div>
              </div>
            </Section>

            <Section
              title="Blocks"
              actions={
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) addBlock(e.target.value as CardBlockType)
                  }}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                >
                  <option value="">+ Add block…</option>
                  {addable.map((type) => (
                    <option key={type} value={type}>
                      {BLOCK_LABELS[type]}
                    </option>
                  ))}
                </select>
              }
            >
              <ul className="space-y-2">
                {content.blocks.map((block, index) => (
                  <li key={block.id} className="rounded-lg border border-gray-200 p-3">
                    <div className={cn('flex items-center gap-2', block.type !== 'divider' && 'mb-2')}>
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{BLOCK_LABELS[block.type]}</span>
                      <span className="flex-1" />
                      <button type="button" disabled={index === 0} onClick={() => moveBlock(index, -1)} className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30" title="Move up">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={index === content.blocks.length - 1}
                        onClick={() => moveBlock(index, 1)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30"
                        title="Move down"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={block.type === 'price' || block.type === 'cta'}
                        onClick={() => removeBlock(index)}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-30"
                        title={block.type === 'price' || block.type === 'cta' ? 'Every card has a price and a button' : 'Remove block'}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {renderBlockFields(block, index)}
                  </li>
                ))}
              </ul>
            </Section>
          </div>

          <div className="xl:sticky xl:top-4 xl:self-start">
            <div className="rounded-xl border border-gray-200 bg-[#F7F9F6] p-4">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Preview</span>
                <select value={sample} onChange={(e) => setSample(Number(e.target.value))} className="rounded border border-gray-300 bg-white px-2 py-1 text-xs">
                  {SAMPLE_PLANS.map((p, i) => (
                    <option key={i} value={i}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="px-2 pb-2 pt-4">
                <PlanCard plan={SAMPLE_PLANS[sample]} card={preview} language={language} cta={{}} />
              </div>
            </div>
          </div>
        </div>

        {error && <div className="rounded-lg border border-red-100 bg-red-50 p-2 text-sm text-red-700">{error.message}</div>}

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4">
          {template && template.planCount > 0 && (
            <span className="mr-auto text-xs text-amber-700">
              Saving changes the card of {template.planCount} plan(s) in {template.organisationCount} organisation(s).
            </span>
          )}
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={save} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : template ? 'Save template' : 'Create template'}
          </Button>
        </div>
      </div>
    </ErrorFieldContext.Provider>
  )
}

export default TemplateEditor
